/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ICache, ICacheItem, ICacheKey } from '@msdyn365-commerce/cache-internal';
import {
    CacheType,
    IActionContext,
    IActionInput,
    IAnyAction,
    IDataActionOptions,
    msdyn365Commerce,
    verboseLogger,
    checkVersionForFeature,
    getPerformanceUtils
} from '@msdyn365-commerce/core-internal';
import { AsyncResult, DataServiceRequest, ErrorTypeEnum, isAsyncResult, ProxyError } from '@msdyn365-commerce/retail-proxy';
import { asSystemMetadata, ITelemetry, LogLevel, StaticTelemetry } from '@msdyn365-commerce/telemetry-internal';
import { get as _get, uniqWith } from 'lodash';
import { toJS } from 'mobx';
import uuidv1 from 'uuid/v1';

import { ActionError, ActionErrorStatus } from './action-error';
import { ActionRegistrar } from './action-registrar';
import { isAppCacheData, shouldReadFromAppCache, shouldWriteToAppCache } from './action-runtime-utils';
import { readCacheEntries, saveCacheEntries } from './cache/retail-cache-helper';
import EventHelper from './event-emitter';

const defaultTimeoutInMS: number = 4000;
const defaultHookTimeoutInMS: number = 1000;
export const CLIENT_HYDRATE_KEY = 'clientHydrateKey';
export const CLIENT_HYDRATE_TYPE = 'clientHydrate';

const Timer = (timeoutInMS: number, message: unknown) => new Promise(resolve => setTimeout(resolve.bind(resolve, message), timeoutInMS));

const ActionTimer = (timeoutInMS: number) =>
    new Promise((_resolve, reject) => setTimeout(reject.bind(reject, ActionErrorStatus.Timeout), timeoutInMS));

const ObservableActionTimer = (timeoutInMS: number) =>
    new AsyncResult((_resolve, reject) => setTimeout(reject.bind(reject, ActionErrorStatus.Timeout), timeoutInMS));

/* @internal */
/**
 * This class contains the logic to batch and de-dupe the actions that can be executed.
 * This class is also responsible for checking and saving the action response to cache.
 */
export default class ActionExecutor {
    public readonly action: Function;
    public readonly cacheType: CacheType;
    private readonly isBatched: boolean;
    private readonly eventId: string;
    private readonly isObservable: boolean;

    private inputs: IActionInput[];
    /**
     * @param action the action to execute
     * @param inputs the action input
     */
    constructor(action: Function, inputs: IActionInput[], telemetry: ITelemetry, dataActionOption?: IDataActionOptions) {
        this.action = action;
        this.eventId = uuidv1();
        this.inputs = [...inputs]; // Cloning it DO NOT use the reference
        this.isBatched = action.prototype.isBatched;

        this.isObservable = action.prototype.isObservable;

        // Cache all the calls on browser
        const masterInput = inputs[0];

        if (masterInput && masterInput.dataCacheType) {
            this.cacheType = masterInput.dataCacheType();
        } else if (masterInput && masterInput.shouldCacheOutput) {
            this.cacheType = masterInput.shouldCacheOutput() ? 'request' : 'none';
            telemetry.trace('shouldCacheOutput property has been deprecated. Please update the IActionInput to use dataCacheType');
        } else {
            this.cacheType = (dataActionOption && dataActionOption.cacheType) || 'none';
            // for data actions who do not have dataCacheType function, we add customer defined cachtype to the action.
            // this is used for later check if the data acton should be read from app cache.
            inputs.forEach(input => {
                input.dataCacheType = () => this.cacheType;
            });
            telemetry.trace('Neither dataCacheType nor shouldCacheOutput are set. Caching strategy will fall back to "none"');
        }
    }

    /**
     *
     * @param inputs add more inputs
     */
    public addInputs(inputs: IActionInput[]): void {
        this.inputs.push(...inputs);
    }

    /** Execute the action  */
    public async execute(
        context: IActionContext,
        requestCache: ICache,
        appCache: ICache | null,
        bypassAppCache?: boolean,
        refreshingInputs: IActionInput[] = []
    ): Promise<IActionInput[]> {
        const unique: IActionInput[] =
            refreshingInputs && refreshingInputs.length > 0
                ? this._removeDuplicates(refreshingInputs)
                : this._removeDuplicates(this.inputs);
        if (!unique.length) {
            return [];
        }

        const actionId = this._getActionIdFromInputs(unique);

        const performanceUtils = getPerformanceUtils(msdyn365Commerce, context);
        const executionId = performanceUtils?.performanceUtilsPackage.Random.Guid.generateGuid();

        performanceUtils?.performanceUtilsPackage.runPreReadonlyDataActionPerformanceHook({
            actionId,
            executionId,
            internalCache: performanceUtils.cache
        });

        const result = await this._executeDataAction(context, requestCache, appCache, bypassAppCache, refreshingInputs, unique, actionId);

        performanceUtils?.performanceUtilsPackage.runPostReadonlyDataActionPerformanceHook({
            actionId,
            executionId,
            internalCache: performanceUtils.cache
        });

        return result;
    }

    private async _executeDataAction(
        context: IActionContext,
        requestCache: ICache,
        appCache: ICache | null,
        bypassAppCache: boolean | undefined,
        refreshingInputs: IActionInput[],
        unique: IActionInput[],
        actionId: string
    ): Promise<IActionInput[]> {
        const isRefreshingAction = refreshingInputs && refreshingInputs.length > 0;

        this._runPreReadHooks(actionId, unique, context)
            .then(() => null)
            .catch(err => null);

        // execte preReaderHook
        this._runPreReaderHooks(actionId, unique, context)
            .then(() => null)
            .catch(err => null);
        const [uncached, needsRefresh]: IActionInput[][] = this._checkForCachedData(
            unique,
            context,
            requestCache,
            appCache,
            bypassAppCache,
            isRefreshingAction
        );

        if (!uncached.length) {
            // excute readOnly post hook with all cached actions ouptput
            await this._fetchOutputsAndRunPostReadHook(actionId, unique, requestCache, context);
            return needsRefresh;
        } else {
            const cachedActions = unique.filter(value => !uncached.includes(value));
            if (cachedActions.length) {
                // fetch the output for all cached data action, and run readOnly hooks. The uncached actions will be excuted later.
                await this._fetchOutputsAndRunPostReadHook(actionId, cachedActions, requestCache, context);
            }
        }

        let outputs: any[] = [];

        // VNext observable promise based flow
        // Create the AsyncResult and immediately return it
        if (this.isObservable) {
            if (this.isBatched) {
                this._executeBatchedObservableActions(uncached, requestCache, appCache, context, isRefreshingAction);
            } else {
                this._executeObservableAction(uncached[0], requestCache, appCache, context, isRefreshingAction);
            }
            return needsRefresh;
        }

        if (this.isBatched) {
            // TODO: Have a max batch count on the action
            outputs = await this._actionWrapper(uncached, context, isRefreshingAction);

            if (outputs && outputs.length !== uncached.length) {
                // TODO Throw
                context.telemetry.log(
                    LogLevel.Trace,
                    'Length of input array {uncached_length} does not meet the output array {outputs_length}',
                    { values: [uncached.length, outputs.length] }
                );
            }
        } else {
            outputs[0] = await this._actionWrapper(uncached[0], context, isRefreshingAction);
        }

        if (!outputs || outputs.length === 0) {
            return needsRefresh;
        }

        this._saveResponseToCache(uncached, outputs, context, requestCache, appCache);
        return needsRefresh;
    }

    // the retail proxy data actions who do not have return type should not interact with cache.
    private _checkShouldInteractWithCache(input: IActionInput, typeName: string | undefined): boolean {
        return !(input instanceof DataServiceRequest) || !!typeName;
    }

    private async _fetchOutputsAndRunPostReadHook(
        actionId: string,
        inputs: IActionInput[],
        requestCache: ICache,
        context: IActionContext
    ): Promise<void> {
        const outputs = inputs.map(i => {
            const cacheKey = { typeName: i.getCacheObjectType(), key: i.getCacheKey() };
            // if the retail data action does not have return type, we do not read the output from cache.
            let cacheItem;
            if (this._checkShouldInteractWithCache(i, cacheKey.typeName)) {
                cacheItem = requestCache.get<ICacheItem<unknown>>(cacheKey);
            }
            return !!cacheItem ? requestCache.getValue(cacheKey) : null;
        });
        this._runPostReadHooks(actionId, inputs, outputs, context)
            .then(() => null)
            .catch(err => null);
        this._runPostReaderHooks(actionId, inputs, outputs, context)
            .then(() => null)
            .catch(err => null);
    }
    private _removeDuplicates(inputs: IActionInput[]): IActionInput[] {
        if (!this.isBatched) {
            return inputs;
        }

        return uniqWith(
            inputs,
            (inputA: IActionInput, inputB: IActionInput) =>
                inputA.getCacheKey() === inputB.getCacheKey() && inputA.getCacheObjectType() === inputB.getCacheObjectType()
        );
    }

    private _getActionIdFromInputs(inputs: IActionInput | IActionInput[]): string {
        let inputsActionId;
        for (const input of Array.isArray(inputs) ? inputs : [inputs]) {
            if (typeof input.getActionId === 'function') {
                inputsActionId = input.getActionId();
            }
        }
        return inputsActionId || (<IAnyAction<unknown>>this.action).prototype.id;
    }

    private _getActionFromId(actionId?: string): Function {
        let { action } = this;
        if (actionId) {
            const registration = ActionRegistrar.resolveRegistration(actionId, <IAnyAction<unknown>>action);
            if (registration) {
                action = <IAnyAction<unknown>>registration.action;
                return ActionRegistrar.unwrapAction(<IAnyAction<unknown>>action);
            }
        }
        return action;
    }

    private async _runPreReadHooks(id: string, inputs: IActionInput | IActionInput[], context: IActionContext): Promise<void> {
        const preReadHooks = ActionRegistrar.getPreReadHooks(id);
        if (!preReadHooks) {
            return;
        }

        const hookTimeout = Number(_get(context, 'requestContext.query.actionHookTimeout')) || defaultHookTimeoutInMS;
        const timer = Timer(hookTimeout, ActionErrorStatus.HookTimeout);

        const preReadHooksArray = Array.from(preReadHooks);
        return preReadHooksArray.reduce(async (previousTask, currentTask) => {
            await previousTask;
            try {
                await Promise.race([timer, currentTask(inputs, context)]);
            } catch (error) {
                const actionId = id || 'unnamed';
                verboseLogger(
                    context && context.requestContext,
                    context.telemetry,
                    "Error execution hook of action_id '{id}', returning null",
                    {
                        values: [asSystemMetadata(actionId)],
                        exception: error
                    },
                    LogLevel.Trace
                );
            }
            return Promise.resolve();
        }, Promise.resolve());
    }

    private async _runPostReaderHooks(
        id: string,
        inputs: IActionInput | IActionInput[],
        actionResult: unknown,
        context: IActionContext
    ): Promise<unknown | void> {
        const postReaderHooks = ActionRegistrar.getPostReaderHooks(id);
        if (!postReaderHooks) {
            return;
        }

        const hookTimeout = Number(_get(context, 'requestContext.query.actionHookTimeout')) || defaultHookTimeoutInMS;
        const timer = Timer(hookTimeout, ActionErrorStatus.HookTimeout);
        const postReaderHooksArray = Array.from(postReaderHooks);
        return postReaderHooksArray.reduce(async (previousTask, currentTask) => {
            await previousTask;
            try {
                await Promise.race([timer, currentTask(inputs, actionResult, context)]);
            } catch (error) {
                const actionId = id || 'unnamed';
                verboseLogger(
                    context && context.requestContext,
                    context.telemetry,
                    "Error execution hook of action_id '{id}', returning null",
                    {
                        values: [asSystemMetadata(actionId)],
                        exception: error
                    },
                    LogLevel.Trace
                );
            }
            return Promise.resolve();
        }, Promise.resolve());
    }

    private async _runPreReaderHooks(id: string, inputs: IActionInput | IActionInput[], context: IActionContext): Promise<void> {
        const preReaderHooks = ActionRegistrar.getPreReaderHooks(id);
        if (!preReaderHooks) {
            return;
        }

        const hookTimeout = Number(_get(context, 'requestContext.query.actionHookTimeout')) || defaultHookTimeoutInMS;
        const timer = Timer(hookTimeout, ActionErrorStatus.HookTimeout);

        const preReaderHooksArray = Array.from(preReaderHooks);
        return preReaderHooksArray.reduce(async (previousTask, currentTask) => {
            await previousTask;
            try {
                await Promise.race([timer, currentTask(inputs, context)]);
            } catch (error) {
                const actionId = id || 'unnamed';
                verboseLogger(
                    context && context.requestContext,
                    context.telemetry,
                    "Error execution hook of action_id '{id}', returning null",
                    {
                        values: [asSystemMetadata(actionId)],
                        exception: error
                    },
                    LogLevel.Trace
                );
            }
            return Promise.resolve();
        }, Promise.resolve());
    }

    private async _runPreHooks(
        id: string,
        inputs: IActionInput | IActionInput[],
        context: IActionContext
    ): Promise<IActionInput | IActionInput[] | void> {
        const preHooks = ActionRegistrar.getPreHooks(id);
        if (!preHooks) {
            return;
        }
        const hookTimeout = Number(_get(context, 'requestContext.query.actionHookTimeout')) || defaultHookTimeoutInMS;
        const timer = Timer(hookTimeout, ActionErrorStatus.HookTimeout);

        const preHooksArray = Array.from(preHooks);
        return preHooksArray.reduce(async (previousTask, currentTask) => {
            const prevInput = await previousTask;
            let tmpNextInput: unknown;
            try {
                tmpNextInput = await Promise.race([timer, currentTask(prevInput, context)]);
            } catch (error) {
                throw error;
            }
            const finalNextInput: IActionInput | IActionInput[] =
                tmpNextInput === ActionErrorStatus.HookTimeout || !tmpNextInput ? prevInput : <IActionInput | IActionInput[]>tmpNextInput;
            return Promise.resolve(finalNextInput);
        }, Promise.resolve(inputs));
    }

    private async _runPostReadHooks(
        id: string,
        inputs: IActionInput | IActionInput[],
        actionResult: unknown,
        context: IActionContext
    ): Promise<unknown | void> {
        const postHooks = ActionRegistrar.getPostReadHooks(id);

        if (!postHooks) {
            return;
        }

        const hookTimeout = Number(_get(context, 'requestContext.query.actionHookTimeout')) || defaultHookTimeoutInMS;
        const timer = Timer(hookTimeout, ActionErrorStatus.HookTimeout);
        const postReadHooksArray = Array.from(postHooks);
        return postReadHooksArray.reduce(async (previousTask, currentTask) => {
            await previousTask;
            try {
                await Promise.race([timer, currentTask(inputs, actionResult, context)]);
            } catch (error) {
                const actionId = id || 'unnamed';
                verboseLogger(
                    context && context.requestContext,
                    context.telemetry,
                    "Error execution hook of action_id '{id}', returning null",
                    {
                        values: [asSystemMetadata(actionId)],
                        exception: error
                    },
                    LogLevel.Trace
                );
            }
            return Promise.resolve();
        }, Promise.resolve());
    }

    private async _runPostHooks(
        id: string,
        inputs: IActionInput | IActionInput[],
        actionResult: unknown,
        context: IActionContext
    ): Promise<unknown | void> {
        const postHooks = ActionRegistrar.getPostHooks(id);
        if (!postHooks) {
            return;
        }
        const hookTimeout = Number(_get(context, 'requestContext.query.actionHookTimeout')) || defaultHookTimeoutInMS;
        const timer = Timer(hookTimeout, ActionErrorStatus.HookTimeout);
        const postHooksArray = Array.from(postHooks);
        return postHooksArray.reduce(async (previousTask, currentTask) => {
            const prevOutput = await previousTask;
            let tmpNextOutput: unknown;
            try {
                tmpNextOutput = await Promise.race([timer, currentTask(inputs, prevOutput, context)]);
            } catch (error) {
                throw error;
            }
            const finalNextOutput = tmpNextOutput === ActionErrorStatus.HookTimeout || !tmpNextOutput ? prevOutput : tmpNextOutput;
            return Promise.resolve(finalNextOutput);
        }, Promise.resolve(actionResult));
    }

    /**
     * Add performance logging before and after calling the action?
     */
    // eslint-disable-next-line complexity
    private async _actionWrapper(inputs: IActionInput | IActionInput[], context: IActionContext, isRefreshing?: boolean): Promise<any> {
        const actionId = this._getActionIdFromInputs(inputs);
        const action = this._getActionFromId(actionId);

        try {
            // Run pre data action hooks
            const _inputs = await this._runPreHooks(actionId, inputs, context);
            if (_inputs) {
                inputs = _inputs;
            }
        } catch (e) {
            const convertedError = this._convertToJSError(e);
            StaticTelemetry.log(
                LogLevel.Error,
                `Error executing pre-hooks for ${
                    isRefreshing ? 'Refreshing' : ''
                } action '{actionName}', returning null. Error '{errorName}'. CorrelationId: '{CorrelationId}'`,
                {
                    values: [
                        actionId || (action && action.name) || 'unnamed',
                        convertedError.name || convertedError.message,
                        (<any>convertedError).data ? (<any>convertedError).data.CorrelationId : ''
                    ],
                    exception: e
                }
            );
            return new ActionError(e);
        }
        let actionResult;
        try {
            const timeOut = Number(_get(context, 'requestContext.query.actionTimeout'));
            const features = _get(context, 'requestContext.features', {});
            const serverSideCustomerTimeout =
                msdyn365Commerce.platformSettings && msdyn365Commerce.platformSettings.serverSideDataActionTimeoutInMs;
            const clientSideCustomerTimeout =
                msdyn365Commerce.platformSettings && msdyn365Commerce.platformSettings.clientSideDataActionTimeoutInMs;
            let promises;
            if (process.env.CURRENT_ENVIRONMENT === 'node' && (features.action_timeout || serverSideCustomerTimeout)) {
                const timeOutValue = (features.action_timeout && timeOut ? timeOut : serverSideCustomerTimeout) || defaultTimeoutInMS;
                promises = [this.isObservable ? ObservableActionTimer(timeOutValue) : ActionTimer(timeOutValue), action(inputs, context)];
            } else if (typeof window !== 'undefined' && msdyn365Commerce.isBrowser && clientSideCustomerTimeout) {
                promises = [
                    this.isObservable ? ObservableActionTimer(clientSideCustomerTimeout) : ActionTimer(clientSideCustomerTimeout),
                    action(inputs, context)
                ];
            } else {
                promises = [action(inputs, context)];
            }
            // run the data action
            actionResult = await Promise.race(promises);
        } catch (e) {
            if (e.message && e.message.length > 0 && !e.message.includes('Action id')) {
                e.message = `Action id: ${actionId}, Error message: ${e.message}`;
            }
            const convertedError = this._convertToJSError(e);
            StaticTelemetry.log(
                LogLevel.Error,
                `Error executing ${
                    isRefreshing ? 'Refreshing' : ''
                } action '{actionName}', returning null. Error '{errorName}'. CorrelationId: '{CorrelationId}'`,
                {
                    values: [
                        actionId || (action && action.name) || 'unnamed',
                        convertedError.name || convertedError.message,
                        (<any>convertedError).data ? (<any>convertedError).data.CorrelationId : ''
                    ],
                    exception: e
                }
            );
            let error = new ActionError(e);
            // For AsyncResult, we need to signal that this was a rejection by throwing
            if (this.isObservable) {
                // Proxy Errors come wrapped as an array
                if (Array.isArray(e)) {
                    error =
                        e[0]._errorCode === ErrorTypeEnum.SERVICE_UNAVAILABLE
                            ? new ActionError(ActionErrorStatus.ServiceUnavailable)
                            : ProxyError.toError(<ProxyError>e[0]);
                } else {
                    error =
                        e._errorCode === ErrorTypeEnum.SERVICE_UNAVAILABLE
                            ? new ActionError(ActionErrorStatus.ServiceUnavailable)
                            : new ActionError(e);
                }
            }

            throw error;
        }

        try {
            // Run post data action hooks
            const result = await this._runPostHooks(actionId, inputs, actionResult, context);
            if (result) {
                actionResult = result;
            }
            this._runPostReadHooks(actionId, inputs, actionResult, context)
                .then(() => null)
                .catch(err => null);
            this._runPostReaderHooks(actionId, inputs, actionResult, context)
                .then(() => null)
                .catch(err => null);
        } catch (e) {
            const convertedError = this._convertToJSError(e);
            StaticTelemetry.log(
                LogLevel.Error,
                `Error executing post-hooks for ${
                    isRefreshing ? 'Refreshing' : ''
                } action '{actionName}', returning null. Error '{errorName}'. CorrelationId: '{CorrelationId}'`,
                {
                    values: [
                        actionId || (action && action.name) || 'unnamed',
                        convertedError.name || convertedError.message,
                        (<any>convertedError).data ? (<any>convertedError).data.CorrelationId : ''
                    ],
                    exception: e
                }
            );
        }
        return actionResult;
    }

    private _shouldUseInstanceCache = () => {
        return checkVersionForFeature('instance_cache') || msdyn365Commerce.platformSettings?.shouldUseInstanceCache;
    };

    private _saveResponseToCache(
        uncached: IActionInput[],
        outputs: any[],
        context: IActionContext,
        requestCache: ICache,
        appCache: ICache | null
    ): void {
        uncached.forEach((i, idx) => {
            const actionId = this._getActionIdFromInputs(i);
            if (i instanceof DataServiceRequest && !!i.getCacheObjectType()) {
                try {
                    const appCacheData = <ICache>appCache;
                    const disableUsingInstanceCache =
                        context.requestContext.features && context.requestContext.features.disable_instance_cache;
                    const usingInstanceCache = !disableUsingInstanceCache && this._shouldUseInstanceCache();
                    saveCacheEntries(i, outputs[idx], requestCache, appCacheData, shouldWriteToAppCache(i, appCache), usingInstanceCache);
                } catch (e) {
                    const { action } = this;
                    const actionName = actionId || (action && action.name) || 'unnamed';
                    verboseLogger(
                        context && context.requestContext,
                        context.telemetry,
                        "Error execution action '{actionName}', returning null",
                        {
                            values: [asSystemMetadata(actionName)],
                            exception: e
                        },
                        LogLevel.Trace
                    );

                    return null;
                }
            } else {
                const entityId = i.getCacheKey();
                const entityType = i.getCacheObjectType();

                if (!!entityId && !!entityType) {
                    const entry = outputs[idx];
                    const cacheKey = { typeName: entityType, key: entityId };
                    if (ActionError.isActionError(entry)) {
                        if (!ActionError.isTimeoutError(entry)) {
                            requestCache.put(cacheKey, { item: (<ActionError>(<unknown>entry)).error, error: true });
                        }
                        // else do nothing
                    } else {
                        if (appCache && shouldWriteToAppCache(i, appCache)) {
                            appCache.put(cacheKey, { item: entry });
                        }
                        const cacheEntry = { item: entry };
                        if (i.dataCacheType && i.dataCacheType() === 'instance') {
                            Object.assign(cacheEntry, { instance: 'instance' });
                        }
                        requestCache.put(cacheKey, cacheEntry);
                    }
                }
            }
        });
    }

    /**
     * Method that executes a set of batchable observable data actions
     * @param uncached The uncached action inputs
     * @param requestCache The request cache
     * @param appCache The application cache, if available
     * @param context The action context
     */
    private _executeBatchedObservableActions(
        uncached: IActionInput[],
        requestCache: ICache,
        appCache: ICache | null,
        context: IActionContext,
        isRefreshing: boolean = false
    ): void {
        const unresolvedSet = new Set();
        const uncachedInputs: IActionInput[] = [];
        // Create an OP for each input in the batch that does not yet exist in cache
        const asyncOutputs = uncached
            .map((input, index) => {
                unresolvedSet.add(index);
                const executor = (resolve: any, reject: any) => {
                    // When the side promise completes, update each individual OP to have it's piece of the entire batched result
                    new Promise((innerResolve, innerReject) => {
                        const onEventIdCompletion = (value: any) => {
                            innerResolve(<{}>value);
                        };

                        const onEventIdFinalCompletion = () => {
                            innerResolve();
                        };

                        const onEventIdFailure = (error: any) => {
                            innerReject(error);
                        };

                        // Standard success event
                        EventHelper.on(`${this.eventId}-complete-${index}-${input.getCacheKey()}`, onEventIdCompletion);

                        // No response closing event
                        EventHelper.on(`${this.eventId}-final-complete-${index}-${input.getCacheKey()}`, onEventIdFinalCompletion);

                        // Failure event
                        EventHelper.on(`${this.eventId}-failure`, onEventIdFailure);
                    })
                        .then(result => {
                            EventHelper.removeAllListeners(`${this.eventId}-complete-${index}-${input.getCacheKey()}`);
                            EventHelper.removeAllListeners(`${this.eventId}-final-complete-${index}-${input.getCacheKey()}`);
                            resolve(result);
                        })
                        .catch(error => {
                            EventHelper.removeAllListeners(`${this.eventId}-failure`);
                            reject(error);
                        });
                };

                // Already cached OP logic (rerun)
                // we only try to fetch the saved promise from cache when the retail proxy data action has cached type
                let cachedPromise;
                if (this._checkShouldInteractWithCache(input, input.getCacheObjectType())) {
                    cachedPromise = requestCache.getValue({ key: input.getCacheKey(), typeName: input.getCacheObjectType() });
                }
                if (cachedPromise && isAsyncResult(<object>cachedPromise) && !isRefreshing) {
                    (<AsyncResult<unknown>>cachedPromise).run(executor);
                    return;
                }

                // Not yet cached OP logic (initialize, run)
                uncachedInputs.push(input);
                return new AsyncResult(executor);
            })
            .filter(Boolean);

        // Save never before cached OPs to cache
        this._saveResponseToCache(uncachedInputs, asyncOutputs, context, requestCache, appCache);

        // Run the actual action
        this._actionWrapper(uncached, context, isRefreshing)
            .then((actionResults: unknown[]) => {
                // Emit events with the associated result so that each individual OP in the cache
                // can be updated appropriately
                actionResults.forEach((actionResult, index) => {
                    unresolvedSet.delete(index);
                    EventHelper.emit(`${this.eventId}-complete-${index}-${uncached[index].getCacheKey()}`, actionResult);
                });
            })
            .catch(error => {
                EventHelper.emit(`${this.eventId}-failure`, error);
            })
            .finally(() => {
                // In the event some inputs do not have results, we need to resolve the promises anyhow
                unresolvedSet.forEach(unresolvedIndex => {
                    const index = <number>unresolvedIndex;
                    EventHelper.emit(`${this.eventId}-final-complete-${index}-${uncached[index].getCacheKey()}`);
                });
            });
    }

    /**
     * Executes a standard (non-batched) observable data action
     * @param input The action input
     * @param requestCache The request cache
     * @param appCache The application cache, if available
     * @param context The action context
     */
    private _executeObservableAction(
        input: IActionInput,
        requestCache: ICache,
        appCache: ICache | null,
        context: IActionContext,
        isRefreshing: boolean = false
    ): void {
        const cacheKey = { key: input.getCacheKey(), typeName: input.getCacheObjectType() };
        const executor = (resolve: any, reject: any) => {
            this._actionWrapper(input, context, isRefreshing)
                .then(result => {
                    resolve(result);
                })
                .catch(error => {
                    // If error is timeout error, lookup app-cache if there
                    // is a valid entity for the given cacheKey in app-cache
                    if (ActionError.isServiceUnavailable(error) && isAppCacheData(input, appCache)) {
                        const cachedItem = appCache?.getValue(cacheKey);
                        if (cachedItem) {
                            return resolve(cachedItem);
                        }
                    }
                    return reject(error);
                });
        };
        // we only try to fetch the saved promise from cache when the retail proxy data action has cached type
        let cachedPromise;
        if (this._checkShouldInteractWithCache(input, input.getCacheObjectType())) {
            cachedPromise = requestCache.getValue({ key: input.getCacheKey(), typeName: input.getCacheObjectType() });
        }
        // If OP is already in cache, just rerun it
        if (cachedPromise && isAsyncResult(<object>cachedPromise) && !isRefreshing) {
            (<AsyncResult<unknown>>cachedPromise).run(executor);
            return;
        }

        // Uncached promises need to be saved to cache
        const asyncOutput = new AsyncResult(executor);
        this._saveResponseToCache([input], [asyncOutput], context, requestCache, appCache);
    }

    private _checkForCachedData(
        inputs: IActionInput[],
        context: IActionContext,
        requestCache: ICache,
        appCache: ICache | null,
        bypassCache?: boolean,
        refreshing?: boolean
    ): IActionInput[][] {
        const mock = _get(context, 'requestContext.query.actionMock');

        if (mock) {
            inputs = this._interceptCacheForMocks(inputs, context, requestCache);
        }

        const clientHydrateCacheKey: ICacheKey = {
            key: CLIENT_HYDRATE_KEY,
            typeName: CLIENT_HYDRATE_TYPE
        };
        const isClientHydrate = requestCache.getValue(clientHydrateCacheKey);
        const disableUsingInstanceCache = context.requestContext.features && context.requestContext.features.disable_instance_cache;
        const usingInstanceCache = !disableUsingInstanceCache && this._shouldUseInstanceCache();
        // if the cache type is set a none don't check cache for data and return inputs for execution
        // lazyresponse == true means it has been called from load-data on browser. In this case, data is already present in request-cache due to run on server side
        if (this.cacheType === 'none' || refreshing) {
            // we should keep read from cache when a dataAction
            // 1. run on server
            // 2. none cache type
            // 3. action running as part of initial page load (client hydration).
            //    This is important to stay consistent with what we render on server.
            // 4. when instance cache feature is on.
            if (!(this.cacheType === 'none' && inputs[0].runOn === 'server' && isClientHydrate && usingInstanceCache) || refreshing) {
                return [inputs, []];
            }
        }

        const uncached: IActionInput[] = [];
        const refresh: IActionInput[] = [];
        // eslint-disable-next-line complexity
        inputs.forEach((i, idx) => {
            const actionId = this._getActionIdFromInputs(i);
            const cacheKey = { typeName: i.getCacheObjectType(), key: i.getCacheKey() };
            // if the retail proxy action does not have return type, we do not check if its in the cache
            if (i instanceof DataServiceRequest && !cacheKey.typeName) {
                uncached.push(i);
                return;
            }
            let cacheItem: ICacheItem<any> | undefined = requestCache.get(cacheKey);
            if (cacheItem && isAsyncResult(<object>cacheItem.item) && (<AsyncResult<unknown>>cacheItem.item).status === 'FAILED') {
                if (!i.runOn || i.runOn !== 'serverOnly') {
                    uncached.push(i);
                }
            } else if (cacheItem && cacheItem.item && cacheItem.item.status === 'LOADING' && cacheItem.item.runOn === 'client') {
                // Ideally, an action configured to runOn client should never interfere with actions running on server.
                // However, it is possible that parent action with runon 'server' could be dependent on an action that is configured
                // by partner to be runOn 'client'. In this case, the child action implicitly becomes a server action. Therefore, lets
                // include such actions in the uncached list so that it gets picked and executed later.
                uncached.push(i);
            } else if (i instanceof DataServiceRequest) {
                try {
                    if (bypassCache && context.requestContext.features && !!!context.requestContext.features.disable_bypass_request_cache) {
                        uncached.push(i);
                    } else {
                        const appCacheData = <ICache>appCache;
                        const cacheEntries = readCacheEntries(
                            i,
                            requestCache,
                            appCacheData,
                            !!!bypassCache && shouldReadFromAppCache(i, appCache),
                            context.requestContext,
                            undefined,
                            !!this.isObservable
                        );
                        if (!cacheEntries.entry) {
                            uncached.push(i);
                        }
                        if (cacheEntries && cacheEntries.shouldRefresh) {
                            refresh.push(i);
                        }
                    }
                } catch (e) {
                    const { action } = this;
                    const actionName = actionId || (action && action.name) || 'unnamed';
                    verboseLogger(
                        context && context.requestContext,
                        context.telemetry,
                        "Error execution action '{actionName}', returning null",
                        {
                            values: [asSystemMetadata(actionName)],
                            exception: e
                        },
                        LogLevel.Trace
                    );
                    uncached.push(i);
                }
            } else {
                if (bypassCache && context.requestContext.features && !context.requestContext.features.disable_bypass_request_cache) {
                    uncached.push(i);
                } else {
                    if (!cacheItem && !!!bypassCache && appCache && shouldReadFromAppCache(i, appCache)) {
                        cacheItem = appCache.copyTo(cacheKey, requestCache, !!this.isObservable);
                        if (cacheItem && cacheItem.shouldRefresh) {
                            refresh.push(i);
                        }
                    }

                    if (!cacheItem || !cacheItem.item) {
                        uncached.push(i);
                        return;
                    }
                }
            }
        });
        return [uncached, refresh];
    }

    /**
     * Searches cache for mocks matching the inputs
     * @param inputs Current action inputs
     * @param context Current action context
     * @param requestCache Current request cache
     */
    private _interceptCacheForMocks(inputs: IActionInput[], context: IActionContext, requestCache: ICache): IActionInput[] {
        const uncached: IActionInput[] = [];
        const actionId: string = this.action.prototype.id;
        inputs.forEach(i => {
            let mockItem = this._getMockItem(i.getCacheObjectType(), i.getCacheKey(), requestCache);
            if (!mockItem && actionId) {
                mockItem = this._getMockItem(actionId, i.getCacheKey(), requestCache);
            }
            let existingItem;
            if (this._checkShouldInteractWithCache(i, i.getCacheObjectType())) {
                existingItem = requestCache.get({ typeName: i.getCacheObjectType(), key: i.getCacheKey() });
            }

            if (!!mockItem && !!mockItem.item && !existingItem) {
                const mockItemJSObject = toJS(mockItem.item);
                if (this.isObservable) {
                    requestCache.put(
                        { typeName: i.getCacheObjectType(), key: i.getCacheKey() },
                        { item: AsyncResult.resolve(mockItemJSObject) }
                    );
                } else {
                    requestCache.put({ typeName: i.getCacheObjectType(), key: i.getCacheKey() }, { item: toJS(mockItemJSObject) });
                }
            } else {
                uncached.push(i);
            }
        });
        return uncached;
    }

    private _getMockItem(cacheNamespace: string, cacheKey: string, requestCache: ICache): ICacheItem<unknown> | undefined {
        return (
            requestCache.get({ typeName: '__MOCK', key: `${cacheNamespace}:${cacheKey}` }) ||
            requestCache.get({ typeName: '__MOCK', key: `${cacheNamespace}:*` })
        );
    }

    private _convertToJSError(e: any): Error {
        // Proxy Error usually comes wrapped in an array
        // If it is, unbox it and conver to regular Error
        if (Array.isArray(e)) {
            const proxyError: ProxyError = e[0];
            if (proxyError.ErrorCode) {
                return ProxyError.toError(proxyError);
            }
        } else if (e.ErrorCode) {
            return ProxyError.toError(e);
        }
        // Return regular Error if not ProxyError
        return new Error(e.message || '');
    }
}
